/**
 * AiHandledBanner
 *
 * Small first-visit dismissible banner shown at the top of major feature
 * panels.  Communicates that Crown & Rose manages all AI prompting —
 * the author never needs to write or understand prompts.
 *
 * Usage:
 *   <AiHandledBanner panelKey="editor" />
 *
 * Dismissal is persisted in localStorage per-panel so it only shows once.
 * Pass `projectId` if you want per-project dismissal instead of global.
 */
import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Per-panel copy ──────────────────────────────────────────────────── */

const PANEL_COPY = {
  editor: {
    headline: 'No AI expertise needed — we handle everything.',
    body: 'Every "Draft Chapter" action sends a precision-crafted prompt carrying your full story context. You write the story; Crown & Rose handles the AI.',
  },
  bible: {
    headline: 'Characters power every AI draft automatically.',
    body: 'Every character you add here is woven into chapter drafts without any extra steps. Just describe your cast — Crown & Rose does the rest.',
  },
  variables: {
    headline: 'Your answers become your AI writing brief.',
    body: 'Genre, tone, POV and style are automatically injected into every prompt Crown & Rose runs for you. Set them once — never think about prompting again.',
  },
  analysis: {
    headline: 'Deep manuscript analysis, fully automated.',
    body: 'Crown & Rose runs a multi-pass AI analysis across your entire manuscript. You read the results and decide what to act on — no prompt writing required.',
  },
  arc: {
    headline: 'Character arcs scored without any configuration.',
    body: 'Arc analysis runs entirely on your manuscript text. Crown & Rose identifies every character mention, scores their journey, and surfaces gaps automatically.',
  },
  marketing: {
    headline: 'Every marketing asset is pre-prompted for your book.',
    body: 'Your Amazon blurb, ARC pitch, tweet thread and social posts are generated using pre-built prompts tuned for indie fiction. Just review and use.',
  },
  flowpass: {
    headline: 'Polish runs on a professional-grade prompt — not yours.',
    body: 'Crown & Rose applies a curated prose-polish prompt to each chapter. Accept the changes you love, skip the ones you don\'t. No prompt knowledge needed.',
  },
  framework: {
    headline: 'Your story blueprint, built without touching a prompt.',
    body: 'Fill in your story details — Crown & Rose turns them into a complete structural framework used in every AI action across your project.',
  },
  kdp: {
    headline: 'Every Amazon/KDP requirement is checked for you.',
    body: "Crown & Rose runs the full KDP compliance checklist automatically. You review the results and fix flagged items — no need to read Amazon's guidelines yourself.",
  },
};

const DEFAULT_COPY = {
  headline: 'Crown & Rose handles all AI prompting for you.',
  body: 'Every action in this panel runs a precision-crafted AI prompt behind the scenes. You focus on your story — we handle the AI.',
};

/* ─── Component ──────────────────────────────────────────────────────── */

export default function AiHandledBanner({ panelKey, projectId, className }) {
  const storageKey = projectId
    ? `cr_ai_banner_${panelKey}_${projectId}`
    : `cr_ai_banner_${panelKey}`;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(storageKey);
    if (!dismissed) setVisible(true);
  }, [storageKey]);

  const dismiss = () => {
    localStorage.setItem(storageKey, '1');
    setVisible(false);
  };

  if (!visible) return null;

  const copy = PANEL_COPY[panelKey] || DEFAULT_COPY;

  return (
    <div
      data-testid={`ai-handled-banner-${panelKey}`}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border border-rose-200/60 bg-gradient-to-r from-rose-50/80 to-white px-3 py-2.5 shadow-sm',
        className,
      )}
    >
      {/* Icon */}
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 ring-1 ring-rose-200">
        <Sparkles className="h-3 w-3 text-rose-600" />
      </div>

      {/* Copy */}
      <div className="flex-1 min-w-0">
        <p className="text-[11.5px] font-semibold text-rose-900 leading-snug">
          {copy.headline}
        </p>
        <p className="mt-0.5 text-[11px] text-rose-800/70 leading-snug">
          {copy.body}
        </p>
      </div>

      {/* Dismiss */}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid={`ai-handled-banner-dismiss-${panelKey}`}
        className="mt-0.5 shrink-0 flex h-5 w-5 items-center justify-center rounded text-rose-400 hover:bg-rose-100 hover:text-rose-700 transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
